import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { UserToNamePipe } from '@shared/pipes/user-to-name.pipe';
import { RackingRuleTransferService } from '@shared/services/export-import-services/racking-rule';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { IRackingConfigs, IRackingConfigsResponse, IResponseRule, IRule, RackingService } from '@shared/services/racking.service';
import { catchError, finalize, map, Subject, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { RackingCreateUpdateRuleDialogComponent } from './racking-create-update-rule/racking-create-update-rule';

@Component({
	selector: 'app-racking-rules',
	templateUrl: './racking.rules.component.html',
	styleUrls: ['./racking.rules.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class RackingRulesComponent implements OnInit {
	public isLoading: boolean = false;
	public showSearch: boolean = false;
	public tableSource = new MatTableDataSource<IRule>();
	public rulesFilter: string = '';
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public selected: object[] = [];
	public unsubscribe: Subject<void> = new Subject<void>();
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	public racksetNames: Map<string, string> = new Map<string, string>();

	// configuration
	public emptySpacesBetweenRulesForm: FormGroup;
	public emptySpacesBetweenRules: number | string | null;
	public componentsForm: FormGroup;
	public components: string;
	private serverConfiguration: IRackingConfigs;

	activeKey = 'general.table.status.active';
	inactiveKey = 'general.table.status.inactive';

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'masterdata.cutting.name', dataKey: 'name' },
		{
			name: 'masterdata.cutting.placementPriority',
			dataKey: 'placementPriority',
		},
		{ name: 'masterdata.cutting.description', dataKey: 'description' },
		{ name: 'masterdata.cutting.batch-type', dataKey: 'batchType' },
		{ name: 'masterdata.cutting.profile', dataKey: 'profile' },
		{
			name: 'masterdata.racking.rules.dialog.slotsPerCell',
			dataKey: 'slotsPerCell',
		},
		{
			name: 'masterdata.cutting.productionSequenceNumberMode.label',
			dataKey: 'productionSequenceNumberModeLabel',
			isTranslatable: true,
			sortAsShown: true,
		},
		{ name: 'masterdata.cutting.rackset', dataKey: 'racksetName' },
		{ name: 'masterdata.cutting.segregation', dataKey: 'segregation' },
		{ name: 'masterdata.cutting.updatedDate', dataKey: 'updatedDate', sortKey: 'updatedAtEpoch' },
		{
			name: 'masterdata.cutting.updatedBy',
			dataKey: 'updatedBy',
			pipe: UserToNamePipe,
			sortAsShown: true,
		},
	];

	public regRowActions: object[] = [
		{
			title: 'masterdata.cutting.actions.edit',
			handler: 'editMasterdataRow',
		},
		{
			title: 'masterdata.cutting.actions.delete',
			handler: 'deleteMasterdataRow',
		},
	];

	favoriteItems: SubMenuItem[] = [
		{
			text: () => 'masterdata.cutting.newbtn.rules',
			icon: () => 'acies_add',
			click: () => this.openCreateUpdateRule(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_export',
			text: () => 'masterdata.racking.ruleImportExport.export.button',
			click: () => this.exportRules(),
			permissions: () => ['racking:RuleList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.racking.ruleImportExport.import.button',
			click: () => this.onImport(),
			permissions: () => ['racking:RulePost', 'racking:RulePut', 'racking:RuleList'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'masterdata.racking.ruleImportExport.import.template.button',
			click: () => this.rackingRuleTransferService.downloadTemplate(),
			permissions: () => ['racking:RuleList'],
		},
	];

	constructor(
		public dialog: MatDialog,
		private translator: TranslateService,
		private rackingService: RackingService,
		private notificationService: NotificationService,
		private fb: FormBuilder,
		private rackingRuleTransferService: RackingRuleTransferService,
		private importValidatorService: ImportValidatorService,
	) {
		this.handleConfigError = this.handleConfigError.bind(this);
		this.emptySpacesBetweenRules = null;
		this.isLoading = true;
		this.components = '';
		this.initializeForm();
	}

	private initializeForm(): void {
		this.emptySpacesBetweenRulesForm = this.fb.group({
			emptySpacesBetweenRules: [this.emptySpacesBetweenRules, [Validators.min(0), Validators.max(99999)]],
		});

		this.componentsForm = this.fb.group({
			components: [this.components, [Validators.max(99999)]],
		});
	}

	public ngOnInit(): void {
		this.isLoading = true;
		this.loadInitialData();
	}

	private loadInitialData(): void {
		this.rackingService
			.getRacksets()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((racksets) => {
				this.racksetNames = new Map(racksets.items.map((rackset) => [rackset.self, rackset.name]));
				this.loadData();
				this.loadConfiguration();
			});
	}

	private loadConfiguration(): void {
		this.isLoading = true;
		this.rackingService
			.getConfigs()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: (response: IRackingConfigsResponse) => {
					this.serverConfiguration = response.items.at(0);
					this.setViewModelFromServerConfiguration();
				},
				error: this.handleConfigError,
				complete: () => {
					this.isLoading = false;
				},
			});
	}

	private handleConfigError(error: HttpErrorResponse) {
		this.isLoading = false;
		// revert back to last loaded server configuration
		this.setViewModelFromServerConfiguration();
		if (error.status === 0) {
			this.notificationService.notify(['masterdata.racking.configs.errors.network'], [], 'error');
		} else {
			this.notificationService.notify(['masterdata.racking.configs.errors.unknown'], [], 'error');
		}
	}

	private setViewModelFromServerConfiguration(): void {
		if (this.serverConfiguration) {
			this.emptySpacesBetweenRules = this.serverConfiguration.emptySpacesBetweenRules;
			this.components = this.serverConfiguration.components.join(',');
		}
	}

	private loadData(): void {
		this.rackingService
			.getRules()
			.pipe(
				takeUntil(this.unsubscribe),
				tap((data) => {
					this.countSubject$.next(data.items.length);
				}),
				map((data) => {
					const rule$ = data.items.map((rule) => {
						return {
							self: rule.self,
							name: rule.name,
							description: rule.description,
							useCoatingInOptimization: rule.useCoatingInOptimization ?? false,
							useCoatingInOptimizationLabel: rule.useCoatingInOptimization ? this.activeKey : this.inactiveKey,
							batchType: rule.batchType?.self ? decodeURIComponent(this.rackingService.getLastSegment(rule.batchType.self)) : '',
							profile: decodeURIComponent(this.rackingService.getLastSegment(rule.profile?.self || '')),
							groupByProductionRoute: rule.groupByProductionRoute,
							productionSequenceNumberMode: rule.productionSequenceNumberMode,
							slotsPerCell: rule.slotsPerCell,
							slotConfiguration: rule.slotConfiguration,
							productionSequenceNumberModeLabel: `masterdata.cutting.productionSequenceNumberMode.${rule.productionSequenceNumberMode}`,
							rackset: rule.rackset?.self,
							racksetName: this.racksetNames.get(rule.rackset?.self),
							chosenRackset: rule.rackset?.self,
							placementPriority: rule.placementPriority,
							segregation: rule.segregation.identifier,
							optimization: rule.optimization.identifier ?? '',
							sorting: rule.sorting,
							material: decodeURIComponent(this.rackingService.getLastSegment(rule.aluminium?.material?.self ?? '')),
							machine: decodeURIComponent(this.rackingService.getLastSegment(rule.aluminium?.machine?.self ?? '')),
							updatedDate: aciesDateFormat(new Date(rule.meta.updatedAt), true, false),
							updatedAtEpoch: rule.meta.updatedAt,
							updatedBy: this.rackingService.getLastSegment(rule.meta.updatedBy.self),
							state: rule.default?.state,
							id0: rule.default?.identifiers[0] ?? '',
							id1: rule.default?.identifiers[1] ?? '',
							id2: rule.default?.identifiers[2] ?? '',
							id3: rule.default?.identifiers[3] ?? '',
							id4: rule.default?.identifiers[4] ?? '',
							id5: rule.default?.identifiers[5] ?? '',
							id6: rule.default?.identifiers[6] ?? '',
							id7: rule.default?.identifiers[7] ?? '',
							id8: rule.default?.identifiers[8] ?? '',
							id9: rule.default?.identifiers[9] ?? '',
							logisticsFormula: rule.logisticsFormula ?? null,
						};
					});
					return rule$;
				}),
				finalize(() => {
					this.isLoading = false;
				}),
			)
			.subscribe((rules$) => {
				this.tableSource.data = rules$;
			});
	}

	public handleSelected(listOfSelected: object[]): void {
		this.selected = listOfSelected;
	}

	public handleRowMenuAction(data: object): void {
		switch (data['action'].handler) {
			case 'editMasterdataRow':
				this.openCreateUpdateRule(data['data']);
				break;
			case 'deleteMasterdataRow':
				this.onDeleteRule(data['data']);
				break;
			default:
				break;
		}
	}

	public handleReloadData(_: object): void {
		// Empty function to enable client side only pagination
		// this.loadData(data['pageSize'] * data['pageNumber']);
	}

	public openCreateUpdateRule(setupData: object = null) {
		const dialogRef = this.dialog.open(RackingCreateUpdateRuleDialogComponent, {
			disableClose: true,
			width: '850px',
			data: setupData,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.loadData();
		});
	}

	private handleError(error: HttpErrorResponse) {
		if (error.status === 0) {
			// A client-side or network error occurred. Handle it accordingly.
			console.error('An error occurred:', error.error);
		} else {
			// The backend returned an unsuccessful response code.
			// The response body may contain clues as to what went wrong.
			console.error(`Backend returned code ${error.status}, body was: `, error.error);
		}
		// Return an observable with a user-facing error message.
		return throwError(() => new Error('Something bad happened; please try again later.'));
	}

	public exportRules(): void {
		this.rackingService
			.getRules()
			.pipe(
				switchMap((rules) => this.rackingRuleTransferService.exportData(rules.items)),
				takeUntil(this.unsubscribe),
			)
			.subscribe({
				next: () => {
					this.notificationService.notify(['masterdata.racking.ruleImportExport.export.notification.success.header'], ['masterdata.racking.ruleImportExport.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export rules', error);
					this.notificationService.notify(['masterdata.racking.ruleImportExport.export.notification.error.header'], ['masterdata.racking.ruleImportExport.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	public onImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'masterdata.racking.ruleImportExport.import.title',
				subHeadingKey: 'masterdata.racking.ruleImportExport.import.subtitle',
				confirmButtonKey: 'masterdata.racking.ruleImportExport.import.button',
				notificationHeader: 'masterdata.racking.ruleImportExport.import.notification.success.header',
				notificationContent: 'masterdata.racking.ruleImportExport.import.notification.success.content',
				notificationErrorHeader: 'masterdata.racking.ruleImportExport.import.notification.error.header',
				notificationErrorContent: 'masterdata.racking.ruleImportExport.import.notification.error.content',
				notificationClientErrorHeader: 'masterdata.racking.ruleImportExport.import.notification.clientError.header',
				notificationClientErrorContent: 'masterdata.racking.ruleImportExport.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.json'],
				validatorService: this.importValidatorService,
				data: this.rackingRuleTransferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(() => {
				this.loadData();
			});
	}

	public onDeleteRule(rule: IResponseRule) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('masterdata.cutting.delete.rules.heading'),
				content: this.translator.instant('masterdata.cutting.delete.rules.content', {
					name: rule.name,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((result) => {
				if (result) {
					this.rackingService
						.delete(rule.self)
						.pipe(catchError(this.handleError), takeUntil(this.unsubscribe))
						.subscribe(() => {
							this.notificationService.notify(['masterdata.cutting.delete.rules.notification.header'], ['masterdata.cutting.delete.rules.notification.content', { name: rule.name }], 'success');
							this.loadData();
						});
				}
			});
	}

	public ngOnDestroy() {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	public updateConfiguration(): void {
		this.updateEmptyRulesFormValidity();
		this.evaluateFormsValidity();
	}

	public get componentsControls() {
		return this.componentsForm.controls;
	}

	public get emptySpacesBetweenRulesControls() {
		return this.emptySpacesBetweenRulesForm.controls;
	}

	private evaluateFormsValidity(): void {
		if (this.isEmptySpacesBetweenRulesFromValid && this.isComponentsFromValid && this.hasConfigurationChanged) {
			this.saveConfiguration();
		}
	}

	public updateEmptyRulesFormValidity(): void {
		const control = this.emptySpacesBetweenRulesForm.get('emptySpacesBetweenRules');
		control.markAsTouched({ onlySelf: true });
		control.updateValueAndValidity();
	}

	public updateComponentsFormValidity(): void {
		const control = this.componentsForm.get('components');
		control.markAsTouched({ onlySelf: true });
		control.updateValueAndValidity();
	}

	private get isEmptySpacesBetweenRulesFromValid(): boolean {
		const control = this.emptySpacesBetweenRulesForm.get('emptySpacesBetweenRules');
		return control.enabled && control.valid;
	}

	private get isComponentsFromValid(): boolean {
		const control = this.componentsForm.get('components');
		return control.enabled && control.valid;
	}

	private saveConfiguration(): void {
		const configs = this.getUpdateConfiguration();
		this.rackingService
			.updateConfig(configs)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe({
				next: () => {
					// save last known server configuration, so we can revert if updating goes wrong
					this.serverConfiguration = configs;
					this.notify('masterdata.racking.configs.update.notification.header', 'masterdata.racking.configs.update.notification.content');
				},
				error: this.handleError,
			});
	}

	private get hasConfigurationChanged(): boolean {
		if (!this.serverConfiguration) {
			return false;
		}

		const configs = this.getUpdateConfiguration();
		return configs.emptySpacesBetweenRules !== this.serverConfiguration.emptySpacesBetweenRules || configs.components.join(',') !== this.serverConfiguration.components.join(',');
	}

	private getUpdateConfiguration(): IRackingConfigs {
		return {
			emptySpacesBetweenRules: this.configEmptySpacesBetweenRules,
			self: this.serverConfiguration.self,
			components: this.components === '' ? [] : this.components.split(','),
		};
	}

	private get configEmptySpacesBetweenRules(): number | null {
		if (this.emptySpacesBetweenRules === null) {
			return null;
		}

		if (typeof this.emptySpacesBetweenRules === 'string') {
			const trimmedValue = this.emptySpacesBetweenRules.trim();
			if (trimmedValue === '') {
				return null;
			}

			const parsedValue = Number(trimmedValue);
			return Number.isNaN(parsedValue) ? null : parsedValue;
		}

		return this.emptySpacesBetweenRules;
	}

	private get configComponents(): string | null {
		if (typeof this.components === 'string' && this.components === '') {
			return null;
		}
		return this.components;
	}

	private notify(header: string, content: string, type: 'error' | 'success' = 'success'): void {
		this.notificationService.notify([header], [content], type);
	}
}
